import { Matrix4, Ray, Vector3 } from '@technology/web3d';
import type {
  AlignmentSegment,
  ClosestAlignmentPoint,
  GeometryInstance,
} from './alignment-geometry.types';
import { Vector3Const } from './web3d-internals';

const VALUES_PER_POINT = 3;
const VALUES_PER_SEGMENT = VALUES_PER_POINT + VALUES_PER_POINT;
const MAX_SEGMENT_FRACTION = 0.9999;

const segmentCountOf = (instance: GeometryInstance): number =>
  (instance.positions?.length ?? 0) / VALUES_PER_SEGMENT;

/** Line segments rather than triangle meshes. */
export function isLineGeometry(instance: GeometryInstance): boolean {
  return (
    instance.positions !== undefined &&
    instance.positions.length % VALUES_PER_SEGMENT === 0 &&
    (instance.normals === undefined || instance.normals.length === 0)
  );
}

/** Web3D chainage at the alignment end: one unit per segment. */
export function countSegments(instances: readonly GeometryInstance[]): number {
  return instances.reduce((sum, instance) => sum + segmentCountOf(instance), 0);
}

const readPoint = (
  positions: ArrayLike<number>,
  offset: number,
  transform: Matrix4,
  worldMatrix: Matrix4
): Vector3 =>
  new Vector3()
    .fromArray(positions, offset)
    .applyMatrix4(transform)
    .applyMatrix4(worldMatrix);

/** The world-space point at a web3d chainage; undefined outside the alignment. */
export function segmentAtChainage(
  instances: readonly GeometryInstance[],
  chainage: number,
  worldMatrix: Matrix4
): AlignmentSegment | undefined {
  if (chainage < 0 || chainage > countSegments(instances)) return undefined;
  let firstSegmentIndex = 0;
  for (const instance of instances) {
    const segmentCount = segmentCountOf(instance);
    const localChainage = chainage - firstSegmentIndex;
    if (
      instance.positions &&
      segmentCount > 0 &&
      localChainage <= segmentCount
    ) {
      // The alignment end belongs to the last segment, not to a segment past it.
      const localIndex = Math.min(Math.floor(localChainage), segmentCount - 1);
      const offset = localIndex * VALUES_PER_SEGMENT;
      const start = readPoint(
        instance.positions,
        offset,
        instance.transform,
        worldMatrix
      );
      const end = readPoint(
        instance.positions,
        offset + VALUES_PER_POINT,
        instance.transform,
        worldMatrix
      );
      return {
        segmentIndex: firstSegmentIndex + localIndex,
        start,
        end,
        point: start.clone().lerp(end, localChainage - localIndex),
      };
    }
    firstSegmentIndex += segmentCount;
  }
  return undefined;
}

/**
 * The alignment point closest to the upward ray from `target`, so that a point below
 * the alignment snaps to it regardless of depth.
 */
export function closestPointOnAlignment(
  instances: readonly GeometryInstance[],
  target: Vector3
): ClosestAlignmentPoint | undefined {
  let closest: ClosestAlignmentPoint | undefined;
  let minDistanceSq = Infinity;
  const ray = new Ray();
  const inverseTransform = new Matrix4();
  const start = new Vector3();
  const end = new Vector3();
  const closestOnSegment = new Vector3();
  let firstSegmentIndex = 0;

  for (const instance of instances) {
    const positions = instance.positions;
    const segmentCount = segmentCountOf(instance);
    if (positions) {
      // Transforms the target to local space rather than every segment to world space.
      inverseTransform.copy(instance.transform).invert();
      ray.origin.copy(target).applyMatrix4(inverseTransform);
      ray.direction.copy(Vector3Const.up).transformDirection(inverseTransform);

      for (let index = 0; index < segmentCount; index++) {
        const offset = index * VALUES_PER_SEGMENT;
        start.fromArray(positions, offset);
        end.fromArray(positions, offset + VALUES_PER_POINT);
        const distanceSq = ray.distanceSqToSegment(
          start,
          end,
          undefined,
          closestOnSegment
        );
        if (distanceSq < minDistanceSq) {
          minDistanceSq = distanceSq;
          const length = start.distanceTo(end);
          const fraction =
            length > 0 ? closestOnSegment.distanceTo(start) / length : 0;
          closest = {
            // Keeps the end point of a segment inside it, so it resolves to that segment.
            web3dChainage:
              firstSegmentIndex +
              index +
              Math.min(MAX_SEGMENT_FRACTION, fraction),
            distance: Math.sqrt(distanceSq),
            point: closestOnSegment.clone().applyMatrix4(instance.transform),
          };
        }
      }
    }
    firstSegmentIndex += segmentCount;
  }
  return closest;
}
